import { Droplets, Wind } from 'lucide-react';
import { WeatherIcon } from './WeatherIcon';
import { WeatherSummaryTooltip } from './WeatherSummaryTooltip';
import type { WeatherState } from '../utils/weatherConditionUtils';

interface ModelInfo {
  label: string;
  color: string;
}

interface WeatherSummaryCellProps {
  weatherState: WeatherState;
  modelInfo: ModelInfo;
  showDayLabel: boolean;
  tooltipTime: string;
}

export function WeatherSummaryCell({
  weatherState,
  modelInfo,
  showDayLabel,
  tooltipTime,
}: WeatherSummaryCellProps) {
  const { condition, timeOfDay, temperature, precipitation, windSpeed } = weatherState;

  return (
    <div
      className={`h-24 w-20 border-b border-r border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors group relative ${
        showDayLabel ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-800'
      }`}
    >
      {/* Hover Tooltip */}
      <WeatherSummaryTooltip
        modelLabel={modelInfo.label}
        time={tooltipTime}
        temperature={temperature}
        precipitation={precipitation ?? 0}
        windSpeed={windSpeed ?? 0}
      />

      {/* Day separator indicator removed per UX request */}

      <div className="p-1 flex flex-col items-center justify-center h-full">
        {/* Weather Icon */}
        <WeatherIcon condition={condition} timeOfDay={timeOfDay} size="sm" />

        {/* Temperature */}
        <div className="text-base font-bold text-gray-900 dark:text-gray-100 mt-0.5">
          {temperature}°
        </div>

        {/* Precipitation/Wind icons - compact */}
        {(precipitation > 0.1 || windSpeed > 2) && (
          <div className="flex items-center gap-1 mt-1">
            {precipitation > 0.1 && (
              <Droplets className="h-2.5 w-2.5" style={{ color: modelInfo.color }} />
            )}
            {windSpeed > 2 && <Wind className="h-2.5 w-2.5" style={{ color: modelInfo.color }} />}
          </div>
        )}
      </div>
    </div>
  );
}

export function WeatherSummaryCellEmpty({ showDayLabel }: { showDayLabel: boolean }) {
  return (
    <div
      className={`h-24 w-20 border-b border-r border-gray-200 dark:border-gray-700 ${
        showDayLabel ? 'bg-gray-100 dark:bg-gray-700' : 'bg-gray-50 dark:bg-gray-800'
      }`}
    />
  );
}
